import { useEffect, useId } from "react";
import { createPortal } from "react-dom";

import { X } from "lucide-react";

import type { FormEvent, ReactNode } from "react";

type ModalProps = {
  title: string;
  children?: ReactNode;
  primaryLabel: string;
  secondaryLabel?: string;
  danger?: boolean;
  pending?: boolean;
  /** Disables only the primary button (e.g. nothing chosen yet). */
  primaryDisabled?: boolean;
  formName?: string;
  className?: string;
  /** A chat thread submits per-message, not through one modal-level action —
   * this skips the primary/secondary button row for that case without
   * touching any of the existing form-modal callers. */
  hideActions?: boolean;
  onPrimary: () => void;
  onCancel: () => void;
};

export const Modal = ({
  title,
  children,
  primaryLabel,
  secondaryLabel = "취소",
  danger = false,
  pending = false,
  primaryDisabled = false,
  formName,
  className,
  hideActions = false,
  onPrimary,
  onCancel,
}: ModalProps) => {
  const titleId = useId();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!pending && !primaryDisabled) {
      onPrimary();
    }
  };

  const actions = (
    <div className="canvaschool-modal-actions">
      <button
        type="button"
        className="canvaschool-btn-secondary"
        onClick={onCancel}
        disabled={pending}
      >
        {secondaryLabel}
      </button>
      <button
        type={formName ? "submit" : "button"}
        className={
          danger ? "canvaschool-btn-danger" : "canvaschool-btn-primary"
        }
        disabled={pending || primaryDisabled}
        onClick={formName ? undefined : () => submit()}
      >
        {primaryLabel}
      </button>
    </div>
  );

  // Excalidraw's own root is a stacking context of its own, so a modal
  // opened from a button rendered inside it (the 마감하기 confirmation,
  // which now lives in Excalidraw's footer row) would be capped at that
  // root's z-index and end up painted under the canvas overlays that sit
  // above it. Mounting on the Canvaschool shell instead escapes that
  // without leaving the subtree the shell's own styles are scoped to.
  const host =
    typeof document === "undefined"
      ? null
      : document.querySelector(".canvaschool-shell");

  const modal = (
    <div className="canvaschool-modal-root">
      <div
        className="canvaschool-modal-backdrop"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div
        className={`canvaschool-modal ${className ?? ""}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="canvaschool-modal-header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="canvaschool-modal-close"
            onClick={onCancel}
            aria-label="닫기"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {formName ? (
          <form aria-label={formName} onSubmit={submit}>
            {children}
            {!hideActions && actions}
          </form>
        ) : (
          <>
            {children}
            {!hideActions && actions}
          </>
        )}
      </div>
    </div>
  );

  return host ? createPortal(modal, host) : modal;
};
